import { Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import {
  siteConfig,
  features,
  categories,
  articles,
  faqItems,
  testimonials,
  stats,
} from '@/mocks/navigation';

function StarryBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute top-[5%] left-[10%] w-[2px] h-[2px] bg-foreground-200/70 rounded-full animate-pulse" style={{ animationDuration: '3s' }}></div>
      <div className="absolute top-[15%] left-[25%] w-[1.5px] h-[1.5px] bg-foreground-200/50 rounded-full animate-pulse" style={{ animationDuration: '4s' }}></div>
      <div className="absolute top-[8%] left-[45%] w-[2px] h-[2px] bg-foreground-200/60 rounded-full animate-pulse" style={{ animationDuration: '2.5s' }}></div>
      <div className="absolute top-[20%] left-[60%] w-[1px] h-[1px] bg-foreground-200/40 rounded-full animate-pulse" style={{ animationDuration: '5s' }}></div>
      <div className="absolute top-[12%] left-[78%] w-[2px] h-[2px] bg-foreground-200/70 rounded-full animate-pulse" style={{ animationDuration: '3.5s' }}></div>
      <div className="absolute top-[25%] left-[35%] w-[1.5px] h-[1.5px] bg-foreground-200/50 rounded-full animate-pulse" style={{ animationDuration: '4.5s' }}></div>
      <div className="absolute top-[18%] left-[88%] w-[1px] h-[1px] bg-foreground-200/30 rounded-full animate-pulse" style={{ animationDuration: '6s' }}></div>
      <div className="absolute top-[30%] left-[15%] w-[2px] h-[2px] bg-foreground-200/60 rounded-full animate-pulse" style={{ animationDuration: '3.2s' }}></div>
      <div className="absolute top-[22%] left-[52%] w-[1.5px] h-[1.5px] bg-foreground-200/45 rounded-full animate-pulse" style={{ animationDuration: '4.8s' }}></div>
      <div className="absolute top-[28%] left-[70%] w-[1px] h-[1px] bg-foreground-200/35 rounded-full animate-pulse" style={{ animationDuration: '5.5s' }}></div>
      <div className="absolute top-[10%] left-[5%] w-16 h-[1px] bg-gradient-to-r from-transparent via-foreground-200/20 to-transparent"></div>
      <div className="absolute top-[24%] left-[82%] w-24 h-[1px] bg-gradient-to-r from-transparent via-foreground-200/15 to-transparent"></div>
    </div>
  );
}

export default function Home() {
  const featuredArticles = articles.slice(0, 3);

  const homepageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${siteConfig.name} - ${siteConfig.tagline}`,
    description: siteConfig.description,
    url: siteConfig.url,
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: siteConfig.name,
      url: siteConfig.url,
    },
    about: {
      '@type': 'SoftwareApplication',
      name: siteConfig.name,
      applicationCategory: 'NavigationApplication',
      operatingSystem: 'iOS, Android',
      description: siteConfig.description,
    },
    mainEntity: {
      '@type': 'FAQPage',
      mainEntity: faqItems.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    },
  };

  return (
    <>
      <SEOHead
        title={`${siteConfig.name} | ${siteConfig.tagline} - 夜间导航软件`}
        description={siteConfig.description}
        keywords="夜色导航,夜间导航软件,夜间驾车导航,暗光地图导航,夜间出行路线规划,护眼夜间地图,夜间户外导航工具"
        canonicalUrl={siteConfig.url}
        schema={homepageSchema}
      />

      <div className="min-h-screen bg-background-50 text-foreground-500">
        <Navbar />

        {/* Hero Section */}
        <header className="relative w-full min-h-screen flex items-center justify-center overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage:
                'url(https://readdy.ai/api/search-image?query=dark%20night%20road%20with%20illuminated%20navigation%20lines%20leading%20into%20the%20distance%2C%20minimalist%20abstract%20style%2C%20warm%20amber%20and%20teal%20light%20trails%20on%20dark%20asphalt%2C%20geometric%20patterns%2C%20futuristic%20aesthetic%2C%20clean%20dark%20atmosphere%2C%20no%20people%2C%20dramatic%20composition&width=1600&height=1000&seq=hero-night-nav-01&orientation=landscape)',
            }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-b from-background-50/70 via-background-50/40 to-background-50"></div>
          <StarryBackground />

          <div className="relative z-10 w-full max-w-4xl mx-auto text-center px-4 md:px-6 py-32">
            <div className="inline-flex items-center gap-2 bg-primary-500/15 border border-primary-500/25 rounded-full px-4 py-1.5 mb-8 animate-[fadeIn_0.8s_ease-out]">
              <div className="w-1.5 h-1.5 bg-primary-500 rounded-full animate-pulse"></div>
              <span className="text-primary-400 text-sm font-medium">夜间出行 · 安全抵达</span>
            </div>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-heading font-bold text-foreground-950 leading-tight mb-6 animate-[fadeIn_1s_ease-out]">
              黑暗中最准确的
              <br />
              <span className="text-primary-500">方向指引</span>
            </h1>

            <p className="text-base md:text-lg text-foreground-400 max-w-2xl mx-auto mb-10 leading-relaxed animate-[fadeIn_1.2s_ease-out]">
              专为夜间出行、暗光环境设计。低蓝光护眼模式、智能路况感知、清晰夜间地图，
              无论夜间驾车、户外探险还是城市夜行，让你在夜色中安全抵达目的地。
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-[fadeIn_1.4s_ease-out]">
              <a
                href="#"
                className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-950 font-semibold px-8 py-3.5 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer text-sm"
              >
                <i className="ri-download-line text-lg"></i>
                立即下载体验
              </a>
              <Link
                to="/articles"
                className="inline-flex items-center gap-2 border border-foreground-300/30 hover:border-foreground-300/60 text-foreground-500 hover:text-foreground-800 px-8 py-3.5 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer text-sm"
              >
                <i className="ri-book-open-line text-lg"></i>
                阅读导航指南
              </Link>
            </div>

            <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 animate-[fadeIn_1.6s_ease-out]">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                    {stat.value}
                  </div>
                  <div className="text-foreground-400 text-xs mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
            <i className="ri-arrow-down-s-line text-foreground-400 text-2xl"></i>
          </div>
        </header>

        {/* Features Section */}
        <section className="py-20 md:py-28 px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                专为<span className="text-primary-500">夜间</span>而生
              </h2>
              <p className="text-foreground-400 max-w-xl mx-auto text-sm md:text-base">
                从显示技术到路线算法，每一个细节都为暗光环境精心优化
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, i) => (
                <div
                  key={feature.id}
                  className="group bg-background-100 hover:bg-background-200/70 border border-background-200/50 rounded-xl p-6 transition-all duration-300 cursor-pointer"
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-accent-500/15 rounded-lg mb-5 group-hover:bg-accent-500/25 transition-colors duration-300">
                    <i className={`${feature.icon} text-accent-500 text-xl`}></i>
                  </div>
                  <h3 className="text-foreground-800 font-semibold text-base mb-2.5">
                    {feature.title}
                  </h3>
                  <p className="text-foreground-400 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="py-20 md:py-28 px-4 md:px-6 bg-background-100/50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                探索你的<span className="text-accent-500">夜间场景</span>
              </h2>
              <p className="text-foreground-400 max-w-xl mx-auto text-sm md:text-base">
                无论你的夜间出行需求是什么，我们都有贴心的解决方案
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group bg-background-50 border border-background-200/50 rounded-xl overflow-hidden transition-all duration-300 hover:border-accent-500/30 cursor-pointer"
                >
                  <div className="h-48 relative overflow-hidden">
                    <img
                      src={cat.image}
                      alt={cat.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/20 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-accent-500 rounded-lg">
                        <i className={`${cat.icon} text-background-950 text-lg`}></i>
                      </div>
                      <span className="text-foreground-950 font-semibold text-base">
                        {cat.name}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <p className="text-foreground-400 text-sm leading-relaxed mb-4">
                      {cat.subtitle}
                    </p>
                    <span className="inline-flex items-center gap-1 text-accent-500 text-sm font-medium group-hover:gap-2 transition-all duration-300">
                      了解更多
                      <i className="ri-arrow-right-line text-xs"></i>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="py-20 md:py-28 px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-12 gap-4">
              <div>
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">
                  夜间出行<span className="text-primary-500">指南</span>
                </h2>
                <p className="text-foreground-400 text-sm">实用的夜间出行知识与技巧</p>
              </div>
              <Link
                to="/articles"
                className="inline-flex items-center gap-1.5 text-primary-500 hover:text-primary-400 text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                查看全部文章
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featuredArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-background-100 border border-background-200/50 rounded-xl overflow-hidden transition-all duration-300 hover:border-primary-500/20 cursor-pointer"
                >
                  <div className="h-44 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-foreground-400 text-xs">{article.date}</span>
                      <span className="w-1 h-1 bg-foreground-400 rounded-full"></span>
                      <span className="text-foreground-400 text-xs">{article.readTime}</span>
                    </div>
                    <h3 className="text-foreground-800 font-semibold text-sm md:text-base mb-2.5 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-foreground-400 text-xs leading-relaxed line-clamp-2">
                      {article.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-20 md:py-28 px-4 md:px-6 bg-background-100/50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                用户<span className="text-accent-500">真实评价</span>
              </h2>
              <p className="text-foreground-400 max-w-xl mx-auto text-sm md:text-base">
                来自不同场景的真实用户反馈，听听他们怎么说
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {testimonials.map((t) => (
                <div
                  key={t.id}
                  className="bg-background-50 border border-background-200/50 rounded-xl p-6"
                >
                  <div className="flex items-center gap-1 mb-4">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${
                          i < t.rating ? 'ri-star-fill text-primary-500' : 'ri-star-fill text-foreground-300'
                        }`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-foreground-500 text-sm leading-relaxed mb-5">
                    {t.content}
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-secondary-500/20 flex items-center justify-center text-secondary-600 font-semibold text-sm">
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <div className="text-foreground-700 text-sm font-medium">{t.name}</div>
                      <div className="text-foreground-400 text-xs">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 md:py-28 px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                常见<span className="text-primary-500">问题</span>
              </h2>
              <p className="text-foreground-400 text-sm md:text-base">
                关于夜色导航，你可能想了解这些
              </p>
            </div>

            <div className="space-y-3">
              {faqItems.map((faq, i) => (
                <details
                  key={i}
                  className="group bg-background-100 border border-background-200/50 rounded-xl overflow-hidden"
                >
                  <summary className="flex items-center justify-between px-6 py-5 cursor-pointer list-none">
                    <span className="text-foreground-700 font-medium text-sm pr-4">
                      {faq.question}
                    </span>
                    <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-background-200/50 group-open:bg-accent-500/20 transition-colors duration-300">
                      <i className="ri-add-line text-foreground-400 group-open:hidden text-xs"></i>
                      <i className="ri-subtract-line text-accent-500 hidden group-open:block text-xs"></i>
                    </span>
                  </summary>
                  <div className="px-6 pb-5 text-foreground-400 text-sm leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 md:py-28 px-4 md:px-6 bg-background-100/50">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
              准备好了吗？
              <br />
              让夜色成为你的<span className="text-primary-500">方向</span>
            </h2>
            <p className="text-foreground-400 text-sm md:text-base mb-8 max-w-lg mx-auto">
              立即下载夜色导航，体验专为夜晚打造的精准导航服务。黑夜不再是障碍，而是你探索世界的全新维度。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="#"
                className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-950 font-semibold px-8 py-3.5 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer text-sm"
              >
                <i className="ri-apple-fill text-lg"></i>
                App Store 下载
              </a>
              <a
                href="#"
                className="inline-flex items-center gap-2 border border-foreground-300/30 hover:border-foreground-300/60 text-foreground-500 hover:text-foreground-800 px-8 py-3.5 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer text-sm"
              >
                <i className="ri-google-play-fill text-lg"></i>
                Google Play 下载
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}